import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LayoutDashboard, HandCoins, TrendingUp, Gift, CalendarDays, FileBarChart, Settings, Menu, LogOut, KeyRound, EyeOff, Church, BellRing, History, MessageSquare, Bell, Wallet, ChevronDown, Landmark, Receipt } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FieldWrap } from "@/components/Field";
import { Logo } from "@/components/Logo";
import { OfflineBanner } from "@/components/OfflineBanner";
import { SessionsDialog } from "@/components/SessionsDialog";
import { MyInfoDialog } from "@/components/MyInfoDialog";
import { MonitorSmartphone, UserRound } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";

export const ADMIN_PANEL_AREAS = ["users", "churches", "settings", "content", "conference", "finance", "export"];

const sectionNav = (s) => [
  { to: `/tithes?s=${s}`, label: "Tithes", icon: HandCoins, feature: "tithes", area: ["records", "reports"] },
  { to: `/progress?s=${s}`, label: "Progress Report", icon: TrendingUp, feature: "progress", area: ["records", "reports"] },
  { id: `${s}-conference`, label: "Conference", icon: CalendarDays, children: [
    { to: `/conference?s=${s}`, label: "Conference Registration", icon: CalendarDays, feature: "conference", area: ["conference"] },
    { to: `/donations?s=${s}`, label: "Conference Collection", icon: Gift, feature: "donations", area: ["records", "reports"] },
  ] },
  { to: `/other?s=${s}`, label: "Other Collection", icon: Gift, admin: true, area: ["other"], setting: `other_${s}` },
  { id: `${s}-reports`, label: "Reports", icon: FileBarChart, children: [["summary", "Summary only"], ["detail", "Full Detail"], ["annual", "Annual Summary"]].map(([v, l]) => (
    { to: `/reports?s=${s}&view=${v}`, label: l, icon: FileBarChart, feature: "reports", perm: `church_view_${v}_${s}`, area: ["reports", "records"], ...(v === "annual" ? { setting: "annual_report" } : {}) })) },
  { to: `/expenses?s=${s}`, label: "Expenses", icon: Wallet, admin: true, area: ["finance", "reports"] },
];

const TOP = [{ to: "/", label: "Dashboard", icon: LayoutDashboard, end: true }];
const GROUPS = [
  { id: "gc", label: "General Council", icon: Landmark, children: sectionNav("gc") },
  { id: "mwjk", label: "MWJK", icon: Church, children: sectionNav("mwjk") },
];
const NAV = [
  { to: "/receipt-book", label: "Receipt Book", icon: Receipt, admin: true, area: ["receipts"] },
  { to: "/messages", label: "Messages", icon: MessageSquare, feature: "messages", area: ["messages"], badge: true },
  { to: "/my-receipts", label: "My receipts", icon: Receipt, churchOnly: true, anyPerm: ["church_view_receipts_gc", "church_view_receipts_mwjk"] },
  { to: "/alerts", label: "Missing Reports", icon: BellRing, admin: true, area: ["reminders"] },
  { to: "/activity", label: "Activity Log", icon: History, admin: true, area: ["all"] },
  { to: "/admin", label: "Admin Panel", icon: Settings, admin: true, area: ADMIN_PANEL_AREAS },
];

const slug = (to) => to.replace(/^\//, "").replace(/[?&=]/g, "-") || "dashboard";

function isHere(to, loc, end) {
  const [path, qs] = to.split("?");
  if (end ? loc.pathname !== path : !loc.pathname.startsWith(path) || (path === "/" && loc.pathname !== "/")) return false;
  const want = new URLSearchParams(qs || "");
  const have = new URLSearchParams(loc.search);
  if (!qs) return !have.get("s");
  return [...want.entries()].every(([k, v]) => (have.get(k) || (k === "view" ? "summary" : "")) === v);
}

const within = (n, loc) => (n.children ? n.children.some((c) => within(c, loc)) : isHere(n.to, loc, n.end));

function NavRow({ n, onNavigate, depth }) {
  const { featureOn, unread } = useApp();
  const loc = useLocation();
  const active = isHere(n.to, loc, n.end);
  return (
    <Link to={n.to} onClick={onNavigate} data-testid={`nav-${slug(n.to)}`} style={{ paddingLeft: `${0.75 + depth * 0.9}rem` }}
      className={`flex items-center gap-3 rounded-lg py-2.5 pr-3 text-[15px] font-medium transition-colors ${active ? "bg-white text-[hsl(var(--primary))]" : "text-rose-100 hover:bg-white/10"}`}>
      <n.icon className="h-5 w-5 shrink-0" />
      <span className="flex-1">{n.label}</span>
      {n.badge && unread > 0 && <span className="rounded-full bg-red-600 px-2 py-0.5 text-xs font-bold text-white" data-testid="nav-messages-badge">{unread}</span>}
      {n.feature && !featureOn(n.feature) && <EyeOff className="h-4 w-4 opacity-60" title="Hidden from church users" />}
    </Link>
  );
}

function NavGroup({ g, onNavigate, depth, allow }) {
  const loc = useLocation();
  const kids = g.children.filter((c) => (c.children ? c.children.some(allow) : allow(c)));
  const [open, setOpen] = useState(() => within(g, loc));
  useEffect(() => { if (within(g, loc)) { setOpen(true); } }, [loc.pathname, loc.search]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!kids.length) return null;
  return (
    <div>
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} data-testid={`nav-group-${g.id}`} style={{ paddingLeft: `${0.75 + depth * 0.9}rem` }}
        className={`flex w-full items-center gap-3 rounded-lg py-2.5 pr-3 text-left transition-colors hover:bg-white/10 ${depth ? "text-[15px] font-medium text-rose-100" : "text-[15px] font-bold uppercase tracking-wide text-white"}`}>
        <g.icon className="h-5 w-5 shrink-0" />
        <span className="flex-1">{g.label}</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className={`mt-1 space-y-1 ${depth ? "" : "border-l border-white/15 ml-5"}`} data-testid={`nav-group-${g.id}-items`}>
          {kids.map((c) => (c.children ? <SubGroup key={c.id} g={c} onNavigate={onNavigate} allow={allow} /> : <NavRow key={c.to} n={c} onNavigate={onNavigate} depth={1} />))}
        </div>
      )}
    </div>
  );
}

function SubGroup({ g, onNavigate, allow }) {
  const loc = useLocation();
  const kids = g.children.filter(allow);
  const [open, setOpen] = useState(() => within(g, loc));
  useEffect(() => { if (within(g, loc)) { setOpen(true); } }, [loc.pathname, loc.search]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!kids.length) return null;
  return (
    <div>
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} data-testid={`nav-group-${g.id}`} style={{ paddingLeft: "1.65rem" }}
        className="flex w-full items-center gap-3 rounded-lg py-2.5 pr-3 text-left text-[15px] font-medium text-rose-100 transition-colors hover:bg-white/10">
        <g.icon className="h-5 w-5 shrink-0" />
        <span className="flex-1">{g.label}</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="mt-1 space-y-1" data-testid={`nav-group-${g.id}-items`}>{kids.map((c) => <NavRow key={c.to} n={c} onNavigate={onNavigate} depth={2} />)}</div>}
    </div>
  );
}

function NavItems({ onNavigate }) {
  const ctx = useApp();
  const allow = (n) => navAllowed(n, ctx) && (!n.setting || ctx.settings?.features?.[n.setting] !== false);
  return (
    <nav className="space-y-1 overflow-y-auto">
      {TOP.filter(allow).map((n) => <NavRow key={n.to} n={n} onNavigate={onNavigate} depth={0} />)}
      {GROUPS.map((g) => <NavGroup key={g.id} g={g} onNavigate={onNavigate} depth={0} allow={allow} />)}
      <div className="my-2 border-t border-white/15" />
      {NAV.filter(allow).map((n) => <NavRow key={n.to} n={n} onNavigate={onNavigate} depth={0} />)}
    </nav>
  );
}

const navAllowed = (n, { isAdmin, featureOn, can, hasArea, user }) => (isAdmin ? !n.churchOnly && (n.badge ? !!user?.can_message : !n.area || hasArea(...n.area) || (n.to === "/admin" && !!user?.can_reset)) : !n.admin && (!n.feature || featureOn(n.feature)) && (!n.perm || can(n.perm)) && (!n.anyPerm || n.anyPerm.some((p) => can(p))));

function MessageBell() {
  const ctx = useApp();
  const navigate = useNavigate();
  const prev = useRef(ctx.unread);
  const allowed = navAllowed(NAV.find((n) => n.badge), ctx);
  useEffect(() => {
    if (allowed && ctx.unread > prev.current) {
      toast("You have a new message", { id: "new-message", action: { label: "Open", onClick: () => navigate("/messages") } });
    }
    prev.current = ctx.unread;
  }, [ctx.unread, allowed, navigate]);
  if (!allowed) return null;
  return (
    <Link to="/messages" className="relative flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-[hsl(var(--primary))] hover:bg-slate-50" data-testid="header-message-bell" aria-label="Messages">
      <Bell className="h-5 w-5" />
      {ctx.unread > 0 && <span className="absolute -right-1 -top-1 min-w-5 rounded-full bg-red-600 px-1.5 ring-2 ring-white text-center text-xs font-bold leading-5 text-white" data-testid="header-unread-count">{ctx.unread}</span>}
    </Link>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-3 px-2" data-testid="sidebar-brand">
      <Logo className="h-12 w-12" testid="sidebar-logo" />
      <div>
        <div className="pt-0.5 font-heading text-3xl font-extrabold leading-tight tracking-tight text-white">MWJK</div>
        <div className="mt-1 text-xs leading-snug text-rose-200">Midwest Jitõbõn Kõmaroñroñ</div>
      </div>
    </div>
  );
}

export function PasswordDialog({ open, onClose }) {
  const [form, setForm] = useState({ current_password: "", new_password: "" });
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/change-password", form);
      toast.success("Password changed");
      setForm({ current_password: "", new_password: "" });
      onClose();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Change my password</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <FieldWrap label="Current password"><Input type="password" className="h-11" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} data-testid="current-password-input" /></FieldWrap>
          <FieldWrap label="New password (6+ characters)"><Input type="password" className="h-11" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} data-testid="new-password-input" /></FieldWrap>
          <Button type="submit" className="w-full" data-testid="change-password-submit">Save new password</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function UserMenu() {
  const { user, isAdmin, isFullAdmin, myChurch, logout } = useApp();
  const [pw, setPw] = useState(false);
  const [sess, setSess] = useState(false);
  const [info, setInfo] = useState(false);
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-3 rounded-full border border-slate-200 bg-white py-1.5 pl-1.5 pr-4 text-left hover:bg-slate-50" data-testid="user-menu-trigger">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[hsl(var(--gold))] font-bold text-white">{user.name?.[0]?.toUpperCase()}</span>
            <span className="hidden sm:block">
              <span className="block text-sm font-semibold leading-tight text-slate-900">{user.name}</span>
              <span className="block text-xs text-slate-500" data-testid="user-role-label">{isAdmin ? (isFullAdmin ? "Master Admin" : "Admin") : myChurch?.name || "Church user"}</span>
            </span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {isAdmin && <DropdownMenuItem onClick={() => setInfo(true)} data-testid="my-info-menu"><UserRound className="mr-2 h-4 w-4" /> My info</DropdownMenuItem>}
          <DropdownMenuItem onClick={() => setPw(true)} data-testid="change-password-menu"><KeyRound className="mr-2 h-4 w-4" /> Change password</DropdownMenuItem>
          <DropdownMenuItem onClick={() => setSess(true)} data-testid="sessions-menu"><MonitorSmartphone className="mr-2 h-4 w-4" /> Signed-in devices</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={logout} data-testid="logout-btn"><LogOut className="mr-2 h-4 w-4" /> Log out</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <PasswordDialog open={pw} onClose={() => setPw(false)} />
      <SessionsDialog open={sess} onClose={() => setSess(false)} />
      <MyInfoDialog open={info} onClose={() => setInfo(false)} />
    </>
  );
}

export default function Layout({ children }) {
  const { isAdmin, myChurch } = useApp();
  const [open, setOpen] = useState(false);
  return (
    <div className="site-bg min-h-screen bg-background">
      <aside className="no-print fixed inset-y-0 left-0 hidden w-64 flex-col gap-8 overflow-y-auto bg-[hsl(var(--primary))] p-5 lg:flex">
        <Brand />
        <NavItems />
      </aside>
      <div className="lg:pl-64">
        <header className="no-print sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-white/10 bg-background/90 site-header px-4 py-3 backdrop-blur-md sm:px-8">
          <div className="flex items-center gap-3">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="lg:hidden" data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 overflow-y-auto border-0 bg-[hsl(var(--primary))] p-5 [&>button]:text-white [&>button]:opacity-90">
                <SheetTitle className="sr-only">Menu</SheetTitle>
                <div className="space-y-8"><Brand /><NavItems onNavigate={() => setOpen(false)} /></div>
              </SheetContent>
            </Sheet>
            <div className="text-on-bg flex items-center gap-2 text-sm font-semibold" data-testid="header-scope">
              <Church className="h-4 w-4 text-[hsl(var(--gold))]" />
              {isAdmin ? "All churches" : myChurch?.name}
            </div>
          </div>
          <div className="flex items-center gap-3"><MessageBell /><UserMenu /></div>
        </header>
        <OfflineBanner />
        <main className="mx-auto max-w-7xl px-4 py-8 sm:px-8 sm:py-10">{children}</main>
      </div>
    </div>
  );
}
